import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components, operations } from "../../types/api";

type ListBriefingsResponse =
  components["schemas"]["ApiResponse_BriefingListResponse_"];
type BriefingDetailResponse =
  components["schemas"]["ApiResponse_BriefingDetailResponse_"];
type BriefingHistoryListResponse =
  components["schemas"]["ApiResponse_BriefingHistoryResponse-Array_"];

type ListBriefingsParams = operations["ListBriefings"]["parameters"]["query"];
type CreateBriefingRequest =
  operations["CreateBriefing"]["requestBody"]["content"]["application/json"];
type UpdateBriefingRequest =
  operations["UpdateBriefing"]["requestBody"]["content"]["application/json"];

type BriefingsByCompanyResponse =
  components["schemas"]["BriefingsByCompanyResponse"];
type BriefingsByCompanyQuery =
  operations["ListBriefingsByCompany"]["parameters"]["query"];

type PlatformCandidateResponse =
  components["schemas"]["ApiResponse_PlatformCandidateResponse_"];
type CreateAssociatedCampaignRequest =
  operations["AddAssociatedCampaign"]["requestBody"]["content"]["application/json"];

type CreateAssociatedCampaignPayload = Omit<
  CreateAssociatedCampaignRequest,
  "sourceId"
> & {
  sourceId?: string | null;
};
type UpdateAssociatedCampaignRequest =
  operations["UpdateAssociatedCampaign"]["requestBody"]["content"]["application/json"];

type AssociatedCampaignResponse =
  components["schemas"]["ApiResponse_AssociatedCampaignResponse_"];

type AssociatedCampaignListResponse =
  components["schemas"]["ApiResponse_AssociatedCampaignListResponse_"];

type DeleteBriefingResponse = components["schemas"]["GenericResponse"];
type AssociatedCampaignGoalMutationResponse =
  components["schemas"]["ApiResponse_GoalResponse_"];
type AssociatedCampaignGoalRequest =
  operations["UpdateAssociatedCampaignGoal"]["requestBody"]["content"]["application/json"];
type AssociatedCampaignGoalParams = {
  briefingId: string;
  associatedId: string;
};

type AssociatedCampaignTargetMutationResponse =
  components["schemas"]["ApiResponse_TargetResponse_"];
type AssociatedCampaignTargetRequest =
  operations["UpdateAssociatedCampaignTarget"]["requestBody"]["content"]["application/json"];
type AssociatedCampaignTargetParams = {
  briefingId: string;
  associatedId: string;
};

type CreateAssociatedCampaignAssetRequest =
  operations["CreateAssociatedCampaignAsset"]["requestBody"]["content"]["application/json"];
type CreateAssociatedCampaignAssetParams = {
  briefingId: string;
  associatedId: string;
};

type CreateAssociatedCampaignSyncRequest =
  operations["CreateAssociatedCampaignSync"]["requestBody"]["content"]["application/json"];

type CreateAssociatedCampaignSyncParams = {
  briefingId: string;
  associatedId: string;
};

type SyncFinancialsResponse =
  operations["SyncFinancials"]["responses"]["200"]["content"]["application/json"];

export type CalculateGoalFinancialsRequest =
  operations["CalculateGoalFinancials"]["requestBody"]["content"]["application/json"];

export type CalculateGoalFinancialsResponse =
  operations["CalculateGoalFinancials"]["responses"]["200"]["content"]["application/json"];

export type AssociatedCampaignTarget = components["schemas"]["TargetResponse"];

export type AssociatedCampaignAsset = components["schemas"]["AssetResponse"];

export type AssociatedCampaignSync = components["schemas"]["SyncResponse"];

export type AssociatedCampaignWithRelations =
  components["schemas"]["AssociatedCampaignResponse"];

export type BriefingTypeDependenciesResponse =
  components["schemas"]["BriefingTypesDependencies"];

/** Single source of truth — generated from backend swagger via openapi-typescript */
export type PlatformMapping = components["schemas"]["PlatformMapping"];

type PlatformMappingsResponse =
  components["schemas"]["ApiResponse_PlatformMapping-Array_"];

export type BriefingMetricsStatus =
  components["schemas"]["BriefingMetricsStatus"];
export type BriefingLineSyncStatus =
  components["schemas"]["BriefingLineSyncStatus"];
export type BriefingLineSyncState =
  components["schemas"]["BriefingLineSyncState"];
export type BriefingMetricsRefreshResult =
  components["schemas"]["BriefingMetricsRefreshResult"];
type BriefingMetricsStatusResponse =
  components["schemas"]["ApiResponse_BriefingMetricsStatus_"];
type BriefingMetricsRefreshResponse =
  components["schemas"]["ApiResponse_BriefingMetricsRefreshResult_"];

export type DataHealthReport = components["schemas"]["DataHealthReport"];
export type DataHealthItem = components["schemas"]["DataHealthItem"];
export type DataHealthState = components["schemas"]["DataHealthState"];
export type DataHealthLineState = components["schemas"]["DataHealthLineState"];
export type DataHealthRefreshResult =
  components["schemas"]["DataHealthRefreshResult"];
type DataHealthResponse =
  components["schemas"]["ApiResponse_DataHealthReport_"];
type DataHealthRefreshResponse =
  components["schemas"]["ApiResponse_DataHealthRefreshResult_"];
/** Periodo YYYY-MM-DD (los dos extremos incluidos) y si traer también las líneas que están bien. */
export type DataHealthQuery = { from?: string; to?: string; all?: boolean };

export const briefingApi = createApi({
  reducerPath: "briefingApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Briefings", "BriefingMetricsStatus", "DataHealth"],
  endpoints: (builder) => ({
    // Salud de datos: líneas de todos los briefings que no tienen los datos al día.
    getDataHealth: builder.query<DataHealthResponse, DataHealthQuery | void>({
      query: (params) => ({ url: "/data-health", params: params ?? undefined }),
      providesTags: ["DataHealth"],
    }),
    refreshDataHealth: builder.mutation<
      DataHealthRefreshResponse,
      { internalCampaignIds?: string[] }
    >({
      query: (body) => ({ url: "/data-health/refresh", method: "POST", body }),
      // Las pedidas pasan a "actualizando" en el estado de su briefing.
      invalidatesTags: ["DataHealth", "BriefingMetricsStatus"],
    }),
    // Estado de sincronización por línea. Tag propio: refrescar el briefing no
    // tiene por qué volver a pedirlo, y el sondeo no debe recargar el briefing.
    getBriefingMetricsStatus: builder.query<
      BriefingMetricsStatusResponse,
      { id: string }
    >({
      query: ({ id }) => `/briefings/${id}/metrics-status`,
      providesTags: (_result, _error, arg) => [
        { type: "BriefingMetricsStatus", id: arg.id },
      ],
    }),
    refreshBriefingMetrics: builder.mutation<
      BriefingMetricsRefreshResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/briefings/${id}/refresh-metrics`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "BriefingMetricsStatus", id: arg.id },
      ],
    }),
    // La lista se guarda 30 min tras salir de la página para volver al instante.
    listBriefings: builder.query<
      ListBriefingsResponse,
      ListBriefingsParams | void
    >({
      keepUnusedDataFor: 30 * 60,
      query: (params) => {
        const url = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                value.forEach((v) => url.append(key, String(v)));
              } else {
                url.append(key, String(value));
              }
            }
          });
        }

        const queryString = url.toString();
        return queryString ? `/briefings?${queryString}` : "/briefings";
      },
      providesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    createBriefing: builder.mutation<
      BriefingDetailResponse,
      CreateBriefingRequest
    >({
      query: (body) => ({
        url: "/briefings",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    getBriefingById: builder.query<
      BriefingDetailResponse,
      { id: string } | void
    >({
      query: ({ id } = { id: "" }) => `/briefings/${id}`,
      providesTags: (_result, _error, arg) =>
        arg?.id ? [{ type: "Briefings", id: arg.id }] : [],
    }),
    getBriefingHistory: builder.query<
      BriefingHistoryListResponse,
      { id: string }
    >({
      query: ({ id }) => `/briefings/${id}/history`,
      providesTags: (_result, _error, arg) =>
        arg?.id ? [{ type: "Briefings", id: arg.id }] : [],
    }),
    getAssociatedCampaign: builder.query<
      AssociatedCampaignResponse,
      { briefingId: string; associatedId: string }
    >({
      query: ({ briefingId, associatedId }) =>
        `/briefings/${briefingId}/associated-campaigns/${associatedId}`,
      providesTags: (_result, _error, arg) =>
        arg?.briefingId ? [{ type: "Briefings", id: arg.briefingId }] : [],
    }),
    createAssociatedCampaignGoal: builder.mutation<
      AssociatedCampaignGoalMutationResponse,
      {
        params: AssociatedCampaignGoalParams;
        body: AssociatedCampaignGoalRequest;
      }
    >({
      query: ({ params, body }) => ({
        url: `/briefings/${params.briefingId}/associated-campaigns/${params.associatedId}/goal`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { params }) =>
        params?.briefingId
          ? [
              { type: "Briefings", id: params.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    updateAssociatedCampaignGoal: builder.mutation<
      AssociatedCampaignGoalMutationResponse,
      {
        params: AssociatedCampaignGoalParams;
        body: AssociatedCampaignGoalRequest;
      }
    >({
      query: ({ params, body }) => ({
        url: `/briefings/${params.briefingId}/associated-campaigns/${params.associatedId}/goal`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { params }) =>
        params?.briefingId
          ? [
              { type: "Briefings", id: params.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    deleteAssociatedCampaignGoal: builder.mutation<
      components["schemas"]["GenericResponse"],
      AssociatedCampaignGoalParams
    >({
      query: ({ briefingId, associatedId }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/goal`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    createAssociatedCampaignTarget: builder.mutation<
      AssociatedCampaignTargetMutationResponse,
      {
        params: AssociatedCampaignTargetParams;
        body: AssociatedCampaignTargetRequest;
      }
    >({
      query: ({ params, body }) => ({
        url: `/briefings/${params.briefingId}/associated-campaigns/${params.associatedId}/target`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { params }) =>
        params?.briefingId
          ? [
              { type: "Briefings", id: params.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    updateAssociatedCampaignTarget: builder.mutation<
      AssociatedCampaignTargetMutationResponse,
      {
        params: AssociatedCampaignTargetParams;
        body: AssociatedCampaignTargetRequest;
      }
    >({
      query: ({ params, body }) => ({
        url: `/briefings/${params.briefingId}/associated-campaigns/${params.associatedId}/target`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { params }) =>
        params?.briefingId
          ? [
              { type: "Briefings", id: params.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    deleteAssociatedCampaignTarget: builder.mutation<
      components["schemas"]["GenericResponse"],
      AssociatedCampaignTargetParams
    >({
      query: ({ briefingId, associatedId }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/target`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    updateAssociatedCampaignStatus: builder.mutation<
      AssociatedCampaignResponse,
      {
        briefingId: string;
        associatedId: string;
        status: components["schemas"]["_36_Enums.AssociatedCampaignStatus"];
      }
    >({
      query: ({ briefingId, associatedId, status }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    deleteAssociatedCampaign: builder.mutation<
      components["schemas"]["GenericResponse"],
      { briefingId: string; associatedId: string }
    >({
      query: ({ briefingId, associatedId }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    duplicateAssociatedCampaign: builder.mutation<
      AssociatedCampaignResponse,
      {
        briefingId: string;
        associatedId: string;
        name: string;
        subcampaignName?: string;
      }
    >({
      query: ({ briefingId, associatedId, name, subcampaignName }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/duplicate`,
        method: "POST",
        body: { name, subcampaignName },
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    updateBriefing: builder.mutation<
      BriefingDetailResponse,
      { id: string; payload: UpdateBriefingRequest }
    >({
      query: ({ id, payload }) => ({
        url: `/briefings/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result?.data?.id
          ? [
              { type: "Briefings", id: "LIST" },
              { type: "Briefings", id: result.data.id },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    duplicateBriefing: builder.mutation<
      BriefingDetailResponse,
      { id: string; payload?: { newStartDate?: string | null } }
    >({
      query: ({ id, payload }) => ({
        url: `/briefings/${id}/duplicate`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    listBriefingsByCompany: builder.query<
      BriefingsByCompanyResponse,
      BriefingsByCompanyQuery | void
    >({
      query: (params) => {
        const url = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                value.forEach((v) => url.append(key, String(v)));
              } else {
                url.append(key, String(value));
              }
            }
          });
        }

        const queryString = url.toString();
        return queryString
          ? `/briefings/by-company?${queryString}`
          : "/briefings/by-company";
      },
      providesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    getPlatformCandidates: builder.mutation<
      PlatformCandidateResponse,
      {
        id: string;
        body: { platformType: components["schemas"]["BriefingPlatform"] };
      }
    >({
      query: ({ id, body }) => ({
        url: `/briefings/${id}/platform-candidates`,
        method: "POST",
        body,
      }),
    }),
    addAssociatedCampaign: builder.mutation<
      AssociatedCampaignResponse,
      { id: string; body: CreateAssociatedCampaignPayload }
    >({
      query: ({ id, body }) => ({
        url: `/briefings/${id}/associated-campaigns`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.id
          ? [
              { type: "Briefings", id: arg.id },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    updateAssociatedCampaign: builder.mutation<
      AssociatedCampaignResponse,
      {
        briefingId: string;
        associatedId: string;
        body: UpdateAssociatedCampaignRequest;
      }
    >({
      query: ({ briefingId, associatedId, body }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.briefingId
          ? [
              { type: "Briefings", id: arg.briefingId },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    getAssociatedCampaigns: builder.query<
      AssociatedCampaignListResponse,
      { id: string }
    >({
      query: ({ id }) => `/briefings/${id}/associated-campaigns`,
      providesTags: (_result, _error, arg) =>
        arg?.id ? [{ type: "Briefings", id: arg.id }] : [],
    }),
    createAssociatedCampaignAsset: builder.mutation<
      void,
      CreateAssociatedCampaignAssetRequest & CreateAssociatedCampaignAssetParams
    >({
      query: ({ briefingId, associatedId, ...body }) => ({
        url: `briefings/${briefingId}/associated-campaigns/${associatedId}/asset`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { briefingId }) => [
        { type: "Briefings", id: briefingId },
        { type: "Briefings", id: "LIST" },
      ],
    }),
    createAssociatedCampaignSync: builder.mutation<
      void,
      CreateAssociatedCampaignSyncRequest & CreateAssociatedCampaignSyncParams
    >({
      query: ({ briefingId, associatedId, ...body }) => ({
        url: `briefings/${briefingId}/associated-campaigns/${associatedId}/sync`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { briefingId }) => [
        { type: "Briefings", id: briefingId },
        { type: "Briefings", id: "LIST" },
      ],
    }),
    fixAssociatedCampaignSync: builder.mutation<
      AssociatedCampaignResponse,
      { briefingId: string; associatedId: string }
    >({
      query: ({ briefingId, associatedId }) => ({
        url: `briefings/${briefingId}/associated-campaigns/${associatedId}/fix-sync`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { briefingId }) => [
        { type: "Briefings", id: briefingId },
        { type: "Briefings", id: "LIST" },
      ],
    }),
    deleteBriefing: builder.mutation<DeleteBriefingResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/briefings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => {
        const tags: { type: "Briefings"; id: string }[] = [
          { type: "Briefings", id: "LIST" },
        ];
        if (arg?.id) {
          tags.push({ type: "Briefings", id: arg.id });
        }
        return tags;
      },
    }),
    getBriefingTypeDependencies: builder.query<
      BriefingTypeDependenciesResponse,
      void
    >({
      query: () => `/briefings/briefing-types-dependencies`,
    }),
    getPlatformMappings: builder.query<PlatformMappingsResponse, void>({
      query: () => `/briefings/platform-mappings`,
    }),
    createBriefingVersion: builder.mutation<
      BriefingDetailResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/briefings/${id}/version`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    updateBriefingStatus: builder.mutation<
      BriefingDetailResponse,
      {
        id: string;
        status: components["schemas"]["BriefingStatus"];
        effectiveDate?: string;
      }
    >({
      query: ({ id, status, effectiveDate }) => ({
        url: `/briefings/${id}/status`,
        method: "PUT",
        body: { status, effectiveDate },
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.id
          ? [
              { type: "Briefings", id: arg.id },
              { type: "Briefings", id: "LIST" },
            ]
          : [{ type: "Briefings", id: "LIST" }],
    }),
    groupCreateCampaigns: builder.mutation<
      AssociatedCampaignListResponse,
      { briefingId: string; associatedCampaignIds: string[] }
    >({
      query: ({ briefingId, associatedCampaignIds }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/group-create`,
        method: "POST",
        body: { associatedCampaignIds },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Briefings", id: arg.briefingId },
      ],
    }),
    singleCreateCampaign: builder.mutation<
      AssociatedCampaignResponse,
      { briefingId: string; associatedId: string }
    >({
      query: ({ briefingId, associatedId }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/create-campaign`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Briefings", id: arg.briefingId },
      ],
    }),
    linkAssociatedCampaign: builder.mutation<
      AssociatedCampaignResponse,
      { briefingId: string; associatedId: string; internalCampaignId: string }
    >({
      query: ({ briefingId, associatedId, internalCampaignId }) => ({
        url: `/briefings/${briefingId}/associated-campaigns/${associatedId}/link`,
        method: "POST",
        body: { internalCampaignId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Briefings", id: arg.briefingId },
      ],
    }),
    syncCampaignFinancials: builder.mutation<SyncFinancialsResponse, void>({
      query: () => ({
        url: "/briefings/sync-financials",
        method: "POST",
      }),
      invalidatesTags: [{ type: "Briefings", id: "LIST" }],
    }),
    calculateGoalFinancials: builder.mutation<
      CalculateGoalFinancialsResponse,
      CalculateGoalFinancialsRequest
    >({
      query: (body) => ({
        url: "/briefings/calculate-goal-financials",
        method: "POST",
        body,
      }),
    }),
    // Admin-only: repair broken briefings
    repairBriefings: builder.mutation<
      components["schemas"]["ApiResponse_RepairBriefingsResult_"],
      { briefingIds: string[] }
    >({
      query: (body) => ({
        url: "/briefings/admin/repair-briefings",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Briefings", id: "LIST" }],
    }),
  }),
});

export const {
  useListBriefingsQuery,
  useListBriefingsByCompanyQuery,
  useCreateBriefingMutation,
  useGetBriefingByIdQuery,
  useGetAssociatedCampaignQuery,
  useCreateAssociatedCampaignGoalMutation,
  useUpdateAssociatedCampaignGoalMutation,
  useDeleteAssociatedCampaignGoalMutation,
  useCreateAssociatedCampaignTargetMutation,
  useUpdateAssociatedCampaignTargetMutation,
  useDeleteAssociatedCampaignTargetMutation,
  useDeleteAssociatedCampaignMutation,
  useUpdateBriefingMutation,
  useDuplicateBriefingMutation,
  useGetPlatformCandidatesMutation,
  useAddAssociatedCampaignMutation,
  useUpdateAssociatedCampaignMutation,
  useGetAssociatedCampaignsQuery,
  useDeleteBriefingMutation,
  useCreateAssociatedCampaignAssetMutation,
  useCreateAssociatedCampaignSyncMutation,
  useFixAssociatedCampaignSyncMutation,
  useGetBriefingTypeDependenciesQuery,
  useGetBriefingHistoryQuery,
  useUpdateBriefingStatusMutation,
  useCreateBriefingVersionMutation,
  useGetPlatformMappingsQuery,
  useGroupCreateCampaignsMutation,
  useSingleCreateCampaignMutation,
  useLinkAssociatedCampaignMutation,
  useDuplicateAssociatedCampaignMutation,
  useUpdateAssociatedCampaignStatusMutation,
  useSyncCampaignFinancialsMutation,
  useCalculateGoalFinancialsMutation,
  useRepairBriefingsMutation,
  useGetBriefingMetricsStatusQuery,
  useRefreshBriefingMetricsMutation,
  useGetDataHealthQuery,
  useRefreshDataHealthMutation,
} = briefingApi;
